// @flow

import * as React from 'react';
import { createUseStyles } from 'react-jss';
import cx from 'classnames';
import Spinner from '../Spinner';
import { Theme } from '_lib/theme';

type Size = 'small';

type Props = {
  children: React.ReactNode;
  icon?: React.ReactNode;
  size?: Size;
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  hasShadow?: boolean;
  testId?: string;
  background?: string;
  color?: string;
  border?: string;
  fullWidth?: boolean;
};

const useStyles = createUseStyles((theme: Theme) => ({
  button: (props: Props) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: props.size === 'small' ? 'auto' : '100%',
    height: props.size === 'small' ? 'auto' : 50,
    margin: 0,
    border: props.border || 'none',
    borderRadius: 100,
    background:
      props.background || 'linear-gradient(90deg, #FF6243 0%, #FF0072 100%)',
    cursor: 'pointer',
    touchAction: 'manipulation',
    padding: props.size === 'small' ? '8px 16px' : '0 70px',
    outline: 'none',
  }),
  disabled: {
    background: '#999 !important',
  },
  text: (props: Props) => ({
    color: props.color || '#fff',
    textTransform: props.size === 'small' ? 'capitalize' : 'uppercase',
    fontSize: 13,
    fontWeight: '700',
    fontFamily: theme.typography.fontFamily,
    letterSpacing: props.size !== 'small' && 0.5,
    whiteSpace: 'nowrap',
    marginLeft: props.icon ? 10 : 0,
  }),
  iconWrapper: {
    position: 'absolute',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    top: 0,
    left: 30,
  },

  container: (props: Props) => {
    let width;
    if (props.fullWidth) {
      width = '100%';
    } else {
      width = 'fit-content';
    }
    return {
      position: 'relative',
      width,
    };
  },
  shadow: {
    width: '86%',
    height: '50%',
    position: 'absolute',
    bottom: 0,
    left: '6%',
    borderRadius: '100%',
    boxShadow: '0 8px 20px 0 rgba(0,0,0,0.2)',
  },
}));

const Button = (props: Props) => {
  const {
    children,
    icon: Icon,
    loading,
    disabled,
    type,
    size,
    onClick,
    hasShadow = true,
    testId,
  } = props;
  const classes = useStyles(props);

  return (
    <div className={classes.container}>
      {hasShadow ? <span className={classes.shadow} /> : null}
      <button
        onClick={e => !disabled && !loading && onClick && onClick(e)}
        className={cx(classes.button, { [classes.disabled]: disabled })}
        type={type}
        test-id={testId}
        disabled={disabled || loading}
      >
        {loading ? (
          <Spinner color="#fff" size={size === 'small' ? 15 : 25} />
        ) : (
          <React.Fragment>
            {Icon && <div className={classes.iconWrapper}>{Icon}</div>}
            <span className={classes.text}>{children}</span>
          </React.Fragment>
        )}
      </button>
    </div>
  );
};

export default Button;
